import {useEffect} from "react";
import {isNotEmpty} from "@Array/isNotEmpty";
import {useAppSelector} from "@Hooks/selector/useAppSelector";
import useHandleCheckOrderError from "@CheckOrder/features/modals/hooks/useHandleCheckOrderError";
import useGoBack from "@Routing/hooks/useGoBack";
import GenericErrorModalType from "@CheckOrder/features/modals/types/GenericErrorModal.type";

interface OpenCheckOrderModalsAttributes {
    openExpiredSessionModal: () => void,
    openPriceChangeModal: () => void,
    openPassengerClassesNoLongerAvailableModal: () => void,
    setGenericError: (value: GenericErrorModalType) => void,
}

/**
 * handles opening of check order modals.
 * Due to the fact that we only want to open one modal at the time (so the opening of each modal is depending on the state of other),
 * we are handling the logic inside the useEffect
 */
export default function useOpenCheckOrderModals(attr: OpenCheckOrderModalsAttributes) {
    const {
        openExpiredSessionModal,
        openPriceChangeModal,
        openPassengerClassesNoLongerAvailableModal,
        setGenericError,
    } = attr

    // TODO if company survives, unify this bs into one redux action prop sth like "type: "priceChangeModal", data: any" and then type data per modal type. this is BS this is pain this is useless
    const {
        ticket,
        checkOrderUserMessages,
        errorOccurred,
        ticketExpired,
        checkOrderModalsVisible: modalActive,
        modalNotice,
    } = useAppSelector((state) => state?.checkOrder)

    const {goBack} = useGoBack()
    const {handleError} = useHandleCheckOrderError()

    const hasSomeCheckOrderUserMessageFromBE = () => (
        isNotEmpty(checkOrderUserMessages) && !ticketExpired && modalActive
    )

    const hasPriceChangeUserMessageFromBE = (checkOrderUserMessages: object) => (
        checkOrderUserMessages["0"].additionalData?.isPriceChanged
    )

    const initializeUserMessageGenericErrorFromBE = (checkOrderUserMessages: object) => {
        const {
            0: {
                error: {userMessage, deliveryType, okBtnText, messageTitle},
            },
        } = checkOrderUserMessages

        const handleClick = () => {
            if (deliveryType === "reload_notice") {
                window.location.reload()
            } else {
                handleError(deliveryType === "alert")
            }
        }

        setGenericError({
            title: messageTitle,
            btnText: okBtnText,
            message: userMessage,
            isInlineHtml: true,
            action: handleClick,
        })
    }

    useEffect(() => {
        if (ticketExpired) {
            openExpiredSessionModal()
            return
        }

        const modals = {
            passengerClassNoLongerAvailable: openPassengerClassesNoLongerAvailableModal,
        }

        if (modals[modalNotice?.name]) {
            modals[modalNotice.name]()
            return;
        }

        if (errorOccurred && !ticket) {
            setGenericError({action: goBack})
            return;
        }

        if (hasSomeCheckOrderUserMessageFromBE()) {
            if (hasPriceChangeUserMessageFromBE(checkOrderUserMessages)) {
                openPriceChangeModal()
                return;
            }

            initializeUserMessageGenericErrorFromBE(checkOrderUserMessages)
            return;
        }

        if (errorOccurred) {
            setGenericError({action: () => handleError(true)})
        }
    }, [ticketExpired, errorOccurred, !!ticket, checkOrderUserMessages, modalActive, modalNotice])
}